import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { supabase } from '../services/supabase';

type EmailType = 'journal_reminders' | 'weekly_digest' | 'newsletter' | 'product_updates' | 'all';

interface UnsubscribeOption {
  id: EmailType;
  label: string;
  description: string;
}

const UNSUBSCRIBE_OPTIONS: UnsubscribeOption[] = [
  { id: 'journal_reminders', label: 'Daily Journal Reminders', description: 'Daily reminders to write in your journal' },
  { id: 'weekly_digest', label: 'Weekly Digest', description: 'Weekly summary of your activity and progress' },
  { id: 'newsletter', label: 'Newsletter', description: 'Tips and insights on personal growth' },
  { id: 'product_updates', label: 'Product Updates', description: 'New features and improvements' },
  { id: 'all', label: 'All Emails', description: 'Unsubscribe from all VZMO emails' },
];

export function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const preselectedType = searchParams.get('type') as EmailType | null;

  const [selectedType, setSelectedType] = useState<EmailType | null>(preselectedType);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Unsubscribe | VZMO';
  }, []);

  async function handleUnsubscribe() {
    if (!selectedType) {
      setError('Please select an email type to unsubscribe from');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (token) {
        const { data, error: rpcError } = await supabase.rpc('unsubscribe_user', {
          p_token: token,
          p_email_type: selectedType,
        });

        if (rpcError) throw rpcError;
        if (!data) throw new Error('Invalid or expired unsubscribe link');
      } else if (email) {
        const { error: updateError } = await supabase
          .from('email_subscribers')
          .update({ is_active: false })
          .eq('email', email.toLowerCase());

        if (updateError) throw updateError;
      } else {
        throw new Error('Invalid unsubscribe link');
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to unsubscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">You have been unsubscribed</h1>
            <p className="text-slate-600 mb-6">
              You will no longer receive {selectedType === 'all' ? 'any' : 'these'} emails from VZMO.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Changed your mind? You can update your email preferences anytime in your account settings.
            </p>
            <div className="space-y-3">
              <Link
                to="/settings"
                className="block w-full py-3 px-4 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
              >
                Go to Settings
              </Link>
              <Link
                to="/"
                className="block w-full py-3 px-4 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <Link
          to="/"
          className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to VZMO
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center mb-6">
            <Mail className="h-7 w-7 text-slate-600" />
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-2">Email Preferences</h1>
          <p className="text-slate-600 mb-6">
            Select which emails you would like to stop receiving.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-3 mb-6">
            {UNSUBSCRIBE_OPTIONS.map((option) => (
              <label
                key={option.id}
                className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedType === option.id
                    ? 'border-coral-500 bg-coral-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="unsubscribe-type"
                    value={option.id}
                    checked={selectedType === option.id}
                    onChange={() => setSelectedType(option.id)}
                    className="mt-1 text-coral-500 focus:ring-coral-500"
                  />
                  <div>
                    <span className={`font-medium ${option.id === 'all' ? 'text-red-700' : 'text-slate-900'}`}>
                      {option.label}
                    </span>
                    <p className="text-sm text-slate-500 mt-0.5">{option.description}</p>
                  </div>
                </div>
              </label>
            ))}
          </div>

          <button
            onClick={handleUnsubscribe}
            disabled={loading || !selectedType}
            className="w-full py-3 px-4 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Processing...
              </>
            ) : (
              'Unsubscribe'
            )}
          </button>

          <p className="text-xs text-slate-500 text-center mt-4">
            You can also manage your email preferences in your{' '}
            <Link to="/settings" className="text-coral-600 hover:underline">
              account settings
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
